import type React from 'react'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { type InviteSignupFormData, useInviteSignup } from '@/hooks/useInviteSignup'
import { Button } from '@/ui/Button'
import { TextInput, useForm, z, zodResolver } from '@/ui/form'
import { Link } from '@/ui/Link'

const acceptInviteSchema = z
    .object({
        name: z.string().min(1, 'Name is required'),
        email: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
        password: z.string().min(8, 'Password must be at least 8 characters long'),
        confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    })

interface InviteSignupFormProps {
    inviteId: string
    showBackButton?: boolean
    onBack?: () => void
    title?: string
    subtitle?: string
}

export const InviteSignupForm: React.FC<InviteSignupFormProps> = ({
    inviteId,
    showBackButton = false,
    onBack,
    title = 'Accept Invitation',
    subtitle = 'Create your account to join the organization',
}) => {
    const { signUpAndAcceptInvite, isLoading, error } = useInviteSignup(inviteId)

    const {
        control,
        handleSubmit,
        formState: { isValid: _isValid },
    } = useForm<InviteSignupFormData>({
        resolver: zodResolver(acceptInviteSchema),
        mode: 'onBlur',
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
    })

    return (
        <View style={styles.container}>
            {showBackButton && onBack && (
                <View style={styles.backButtonContainer}>
                    <Button title="← Back" mode="outlined" onPress={onBack} />
                </View>
            )}

            <View style={styles.headerContainer}>
                <Text variant="headlineLarge">{title}</Text>
                <Text variant="bodyMedium" style={styles.subtitle}>
                    {subtitle}
                </Text>
            </View>

            <View style={styles.formContainer}>
                <TextInput
                    name="name"
                    control={control}
                    label="Full Name"
                    placeholder="Enter your full name"
                    autoCapitalize="words"
                    autoComplete="name"
                />

                <TextInput
                    name="email"
                    control={control}
                    label="Email Address"
                    placeholder="Enter your email"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                />

                <TextInput
                    name="password"
                    control={control}
                    label="Password"
                    placeholder="Create a password"
                    secureTextEntry
                    autoComplete="new-password"
                />

                <TextInput
                    name="confirmPassword"
                    control={control}
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    secureTextEntry
                    autoComplete="new-password"
                />

                {error && (
                    <Text variant="bodyMedium" style={styles.errorText}>
                        {error}
                    </Text>
                )}

                <Button
                    title="Create Account"
                    mode="contained"
                    onPress={handleSubmit(signUpAndAcceptInvite)}
                    disabled={isLoading}
                    loading={isLoading}
                    loadingTitle="Creating Account..."
                    style={styles.submitButton}
                />
            </View>

            {!showBackButton && (
                <View style={styles.signInPrompt}>
                    <Text variant="bodyMedium" style={styles.promptText}>
                        Already have an account?
                    </Text>
                    <Link href="/auth/sign-in">
                        <Text variant="bodyMedium" style={styles.linkText}>
                            Sign In
                        </Text>
                    </Link>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 16,
        gap: 16,
        maxWidth: 400,
        width: '100%',
        alignSelf: 'center',
    },
    backButtonContainer: {
        gap: 8,
        marginBottom: 8,
    },
    headerContainer: {
        gap: 8,
        marginBottom: 16,
    },
    subtitle: {
        color: '#374151',
    },
    formContainer: {
        gap: 16,
    },
    errorText: {
        color: '#dc2626',
    },
    submitButton: {
        paddingVertical: 8,
    },
    signInPrompt: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 16,
    },
    promptText: {
        color: '#374151',
    },
    linkText: {
        color: '#3b82f6',
    },
})
